import React, { useEffect, useMemo, useState } from 'react';
import { TYPE_COLOR } from './ArenaBoardIso';
import {
  AIM_LABELS,
  ITEM_MAP,
  LINES,
  LINE_MAP,
  RECIPE_LIST,
  ROSTER_LINES,
  RUN_POOL_SIZE,
  SHOP_ODDS,
  SHOP_SIZE,
  STARTER_LINES,
  TYPES,
  lineSynergyRows,
  linePlaystyle,
  rangeLabel,
  traitBonus,
  traitCap,
  traitDossier,
  canMegaEvolve,
  craftResult,
  craftsWith,
  unitFromLine
} from './pokeArenaData';
import { NODE_INFO, isFightKind, regionAt, regionsOf, trainerSprite } from './pokeArenaCampaign';

export const ROLE_COLOR = { Starter: '#e9a23b', Force: '#d1495b', Mage: '#5c7cfa' };
export const tint = (t) => TYPE_COLOR[t] || ROLE_COLOR[t] || '#9aa3c2';
export const stars = (n) => '★'.repeat(n);
/** Nom sans les ★ des formes répétées (les étoiles sont affichées à part). */
export const displayName = (name = '') => name.replace(/\s*★+$/, '');

const GENS = [[1, 151], [152, 251], [252, 386], [387, 493], [494, 649], [650, 721], [722, 809], [810, 905], [906, 1025]];
const genOf = (dex) => {
  const i = GENS.findIndex(([a, b]) => dex >= a && dex <= b);
  return i < 0 ? 0 : i + 1;
};

/* ———————————————— Petites briques ———————————————— */

export const PokeSprite = ({ unit, size = 'md', className = '' }) => {
  const chain = [unit?.home, unit?.art, unit?.sprite].filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [unit?.dex]);
  if (!unit) return null;
  return (
    <img
      className={`ui-sprite size-${size}${unit.star >= 3 ? ' is-gold' : ''} ${className}`}
      src={chain[Math.min(idx, chain.length - 1)]}
      alt={displayName(unit.name)}
      draggable={false}
      loading="lazy"
      onError={() => setIdx((i) => Math.min(i + 1, chain.length - 1))}
    />
  );
};

export const ItemIcon = ({ item, size = 28 }) => {
  const [fail, setFail] = useState(false);
  if (!item) return null;
  if (item.sprite && !fail) {
    return (
      <img
        src={item.sprite}
        alt={item.name}
        className="ui-item-img"
        style={{ width: size, height: size }}
        draggable={false}
        onError={() => setFail(true)}
      />
    );
  }
  return <span className="ui-item-fallback" style={{ width: size, height: size }}>{item.name.slice(0, 1)}</span>;
};

export const TypeChip = ({ type, small, onHover }) => (
  <span
    className={`ui-type${small ? ' is-small' : ''}`}
    style={{ '--t': tint(type) }}
    onMouseEnter={(e) => onHover?.(type, e)}
    onMouseLeave={() => onHover?.(null)}
  >
    {type}
  </span>
);

const StatRow = ({ label, value, max }) => (
  <div className="ui-stat">
    <span>{label}</span>
    <i><b style={{ width: `${Math.min(100, (value / max) * 100)}%` }} /></i>
    <strong>{value}</strong>
  </div>
);

/** Fiche détaillée d’un Pokémon (survol / sélection). */
export const UnitCard = ({ unit, onTrait, compact = false, footer = null }) => {
  if (!unit) return null;
  const types = unit.types || [unit.type];
  const roles = (unit.traits || []).filter((t) => !types.includes(t));
  const plates = (unit.items || []).map((id) => ITEM_MAP[id]?.addTrait).filter(Boolean);
  return (
    <article className={`ui-card${compact ? ' is-compact' : ''}`} style={{ '--t': tint(unit.type) }}>
      <header className="ui-card-head">
        <div className="ui-card-art">
          <PokeSprite unit={unit} size="lg" />
        </div>
        <div className="ui-card-id">
          <h3>{displayName(unit.name)}</h3>
          <p>
            <span className="ui-stars">{stars(unit.star)}</span>
            <span className={`ui-cost cost-${unit.cost}`}>{unit.cost} or</span>
            <span className="ui-range">{rangeLabel(unit.range)}</span>
          </p>
          <div className="ui-chips">
            {[...types, ...roles, ...plates.filter((p) => !types.includes(p) && !roles.includes(p))].map((t) => (
              <TypeChip key={t} type={t} small onHover={onTrait} />
            ))}
          </div>
        </div>
      </header>
      <div className="ui-stats">
        <StatRow label="PV" value={unit.hp} max={1800} />
        <StatRow label="ATK" value={unit.atk} max={260} />
        <StatRow label="VIT" value={unit.spd} max={22} />
      </div>
      {unit.spell && (
        <section className="ui-ability">
          <h4>{unit.spell.name}</h4>
          <p>{unit.spell.desc}</p>
        </section>
      )}
      {unit.ab && (
        <section className="ui-ability is-talent">
          <h4>Talent : {unit.ab.name}</h4>
          <p>{unit.ab.desc}</p>
        </section>
      )}
      {(unit.items || []).length > 0 && (
        <ul className="ui-card-items">
          {unit.items.map((id, i) => {
            const it = ITEM_MAP[id];
            if (!it) return null;
            return (
              <li key={`${id}-${i}`}>
                <ItemIcon item={it} size={26} />
                <div><b>{it.name}</b><span>{it.desc}</span></div>
              </li>
            );
          })}
        </ul>
      )}
      {footer}
    </article>
  );
};

/** Bulle d’explication d’une synergie. */
export const TraitPopup = ({ pop }) => {
  if (!pop?.trait) return null;
  const doc = traitDossier(pop.trait);
  return (
    <div className="ui-pop" style={{ left: pop.left, top: pop.top, '--t': tint(pop.trait) }} role="tooltip">
      <header>
        <strong>{doc.title}</strong>
        {pop.count != null && <em>{pop.count} sur le terrain</em>}
      </header>
      {doc.blurb && <p>{doc.blurb}</p>}
      {doc.tiers.length > 0 && (
        <ul>
          {doc.tiers.map((tier) => (
            <li key={tier.n} className={pop.count >= tier.n ? 'is-on' : ''}>{tier.label}</li>
          ))}
        </ul>
      )}
      {doc.strong.length > 0 && <p><b>Efficace contre</b> {doc.strong.map((r) => r.name).join(', ')}</p>}
      {doc.weak.length > 0 && <p><b>Peu efficace contre</b> {doc.weak.map((r) => r.name).join(', ')}</p>}
    </div>
  );
};

/** Panneau des synergies actives (à gauche du plateau). */
export const TraitPanel = ({ units, onTrait }) => {
  const info = useMemo(() => traitBonus(units), [units]);
  const rows = Object.entries(info.counts || {})
    .map(([trait, count]) => ({ trait, count, cap: traitCap(trait), active: count >= 2 }))
    .sort((a, b) => Number(b.active) - Number(a.active) || b.count - a.count || a.trait.localeCompare(b.trait, 'fr'));
  return (
    <aside className="ui-traits" aria-label="Synergies">
      <h2>Synergies</h2>
      {!rows.length && <p className="ui-empty">Place des Pokémon sur le terrain : 2 du même type activent un bonus.</p>}
      <ul>
        {rows.map((r) => {
          const tip = info.details?.find((d) => d.trait === r.trait)?.tip || '';
          return (
            <li
              key={r.trait}
              className={r.active ? 'is-active' : ''}
              style={{ '--t': tint(r.trait) }}
              onMouseEnter={(e) => onTrait?.(r.trait, e, { count: r.count, tip })}
              onMouseLeave={() => onTrait?.(null)}
            >
              <span className="ui-trait-badge">{r.count}</span>
              <div>
                <strong>{r.trait}</strong>
                <span className="ui-pips" aria-hidden>
                  {Array.from({ length: Math.max(2, r.cap) }).map((_, i) => <i key={i} className={i < r.count ? 'on' : ''} />)}
                </span>
                <em>{r.active ? tip : `encore ${2 - r.count} pour activer`}</em>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

const useEscape = (fn) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') fn(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fn]);
};

export const RecipeModal = ({ onClose }) => {
  useEscape(onClose);
  return (
  <div className="ui-modal" role="dialog" aria-label="Recettes d’objets" onClick={onClose}>
    <div className="ui-modal-card is-wide" onClick={(e) => e.stopPropagation()}>
      <header>
        <h2>Recettes</h2>
        <button type="button" className="ui-btn is-ghost" onClick={onClose}>Fermer</button>
      </header>
      <p className="ui-muted">Deux vitamines sur le même Pokémon se combinent automatiquement.</p>
      <ul className="ui-recipes">
        {RECIPE_LIST.map((row) => (
          <li key={`${row.a}-${row.b}`}>
            <span className="ui-recipe-in">
              <ItemIcon item={ITEM_MAP[row.a]} size={26} />
              <b>+</b>
              <ItemIcon item={ITEM_MAP[row.b]} size={26} />
            </span>
            <ItemIcon item={row.result} size={34} />
            <div>
              <strong>{row.result.name}</strong>
              <span>{row.result.desc}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
  );
};

/* ———————————————— Choix du starter ———————————————— */

const evolutionPreview = (line) => {
  const pre = (line.forms ? line.stages.slice(0, line.branchAt - 1) : line.stages)
    .filter((st, i, arr) => i === 0 || st.dex !== arr[i - 1].dex);
  const branches = (line.forms || []).map((f) => f.stages[0]);
  return { pre, branches };
};

const MiniStage = ({ stage }) => (
  <figure className="ui-evo-stage">
    <PokeSprite unit={{ home: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/home/${stage.dex}.png`, art: `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${stage.dex}.png`, dex: stage.dex, name: stage.name }} size="md" />
    <figcaption>{displayName(stage.name)}</figcaption>
  </figure>
);

/* ———————————————— Ton dresseur ———————————————— */

/** Dresseurs jouables (sprites Showdown), choisis au démarrage et affichés de ton côté du terrain. */
/* Liste vérifiée sur les sprites Showdown (noms français officiels) ; `sprite` = essais dans l'ordre. */
export const PLAYER_TRAINERS = [
  { id: 'red', name: 'Red', gen: 1, sprite: ["red", "red-gen1"] },
  { id: 'leaf', name: 'Leaf', gen: 1, sprite: ["leaf-gen3", "leaf-masters"] },
  { id: 'blue-gen7', name: 'Blue', gen: 1, sprite: ["blue-gen7", "blue", "blue-lgpe"] },
  { id: 'ethan', name: 'Ethan', gen: 2 },
  { id: 'kris', name: 'Kris', gen: 2, sprite: ["kris-gen2", "kris"] },
  { id: 'lyra', name: 'Lyra', gen: 2 },
  { id: 'silver', name: 'Silver', gen: 2 },
  { id: 'brendan', name: 'Brice', gen: 3, sprite: ["brendan-gen3", "brendan-contest", "brendan"] },
  { id: 'may', name: 'Flora', gen: 3, sprite: ["may-gen3", "may-contest", "may"] },
  { id: 'wally', name: 'Timmy', gen: 3, sprite: ["wally", "wally-gen3"] },
  { id: 'steven', name: 'Pierre Rochard', gen: 3, sprite: ["steven", "steven-gen3"] },
  { id: 'lucas', name: 'Louka', gen: 4 },
  { id: 'dawn', name: 'Aurore', gen: 4 },
  { id: 'barry', name: 'Barry', gen: 4 },
  { id: 'cynthia', name: 'Cynthia', gen: 4 },
  { id: 'cyrus', name: 'Hélio', gen: 4 },
  { id: 'hilbert', name: 'Ludwig', gen: 5 },
  { id: 'hilda', name: 'Ludvina', gen: 5 },
  { id: 'cheren', name: 'Tcheren', gen: 5 },
  { id: 'bianca', name: 'Bianca', gen: 5 },
  { id: 'n', name: 'N', gen: 5 },
  { id: 'nate', name: 'Mélis', gen: 5 },
  { id: 'rosa', name: 'Écho', gen: 5 },
  { id: 'hugh', name: 'Matis', gen: 5 },
  { id: 'calem', name: 'Kalem', gen: 6, sprite: ["calem", "calem-masters"] },
  { id: 'serena', name: 'Serena', gen: 6, sprite: ["serena", "serena-masters"] },
  { id: 'shauna', name: 'Shauna', gen: 6 },
  { id: 'diantha', name: 'Dianthéa', gen: 6 },
  { id: 'elio', name: 'Elio', gen: 7, sprite: ["elio", "elio-usum"] },
  { id: 'selene', name: 'Selene', gen: 7, sprite: ["selene", "selene-usum"] },
  { id: 'hau', name: 'Tili', gen: 7, sprite: ["hau", "hau-masters"] },
  { id: 'gladion', name: 'Gladio', gen: 7, sprite: ["gladion", "gladion-stance", "gladion-masters"] },
  { id: 'lillie', name: 'Lilie', gen: 7, sprite: ["lillie", "lillie-z", "lillie-masters"] },
  { id: 'victor', name: 'Victor', gen: 8 },
  { id: 'gloria', name: 'Gloria', gen: 8 },
  { id: 'hop', name: 'Nabil', gen: 8 },
  { id: 'marnie', name: 'Rosemary', gen: 8, sprite: ["marnie", "marnie-league"] },
  { id: 'bede', name: 'Travis', gen: 8, sprite: ["bede", "bede-leader"] },
  { id: 'leon', name: 'Tarak', gen: 8, sprite: ["leon", "leon-masters"] },
  { id: 'florian', name: 'Florian', gen: 9, sprite: ["florian-s", "florian-bb"] },
  { id: 'juliana', name: 'Juliana', gen: 9, sprite: ["juliana-s", "juliana-bb"] },
  { id: 'nemona', name: 'Menzi', gen: 9, sprite: ["nemona-v", "nemona-s"] },
  { id: 'arven', name: 'Pepper', gen: 9, sprite: ["arven-v", "arven-s"] },
  { id: 'penny', name: 'Pania', gen: 9 },
  { id: 'geeta', name: 'Alisma', gen: 9 }
];

export const playerTrainerSprite = (id) => {
  const t = PLAYER_TRAINERS.find((x) => x.id === id);
  return (t && (Array.isArray(t.sprite) ? t.sprite[0] : t.sprite)) || id;
};
const TRAINER_GENS = [[0, 'Tous'], [1, 'Kanto'], [2, 'Johto'], [3, 'Hoenn'], [4, 'Sinnoh'], [5, 'Unys'], [6, 'Kalos'], [7, 'Alola'], [8, 'Galar'], [9, 'Paldea']];

/* Unité de mise en page des écrans « maquette » (1280×720 mis à l'échelle de la fenêtre) */
const stageUnit = () => (typeof window === 'undefined' ? 1 : Math.max(0.55, Math.min(window.innerWidth, (window.innerHeight * 16) / 9) / 1280));
export const useStageUnit = () => {
  const [u, setU] = useState(stageUnit);
  useEffect(() => {
    const on = () => setU(stageUnit());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return u;
};
const REGION_OF_GEN = ['', 'Kanto', 'Johto', 'Hoenn', 'Sinnoh', 'Unys', 'Kalos', 'Alola', 'Galar', 'Paldea'];
const trainerFace = (t) => ({ sprite: t.sprite || t.id, name: t.name });
/** Sprite de dresseur en grand (remplit sa case), avec les sprites de secours. */
const TrainerImg = ({ t, className = '' }) => {
  const list = (Array.isArray(t.sprite) ? t.sprite : [t.sprite || t.id]).filter(Boolean);
  const [idx, setIdx] = useState(0);
  if (idx >= list.length) return <span className={`tp2-img is-fb ${className}`}>{(t.name || '?').slice(0, 1)}</span>;
  return <img className={`tp2-img ${className}`} src={trainerSprite(list[idx])} alt="" draggable={false} loading="lazy" onError={() => setIdx((i) => i + 1)} />;
};

/** Écran plein « Choisis ton dresseur » (maquette Tactics D+F). */
export const TrainerScreen = ({ value, onChange, onClose }) => {
  const u = useStageUnit();
  const [gen, setGen] = useState(0);
  const [pick, setPick] = useState(value || PLAYER_TRAINERS[0].id);
  const list = PLAYER_TRAINERS.filter((t) => !gen || t.gen === gen);
  const cur = PLAYER_TRAINERS.find((t) => t.id === pick) || PLAYER_TRAINERS[0];
  const choose = (id) => { onChange(id); onClose(); };
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="ah2 tp2" style={{ '--u': `${u}px` }} role="dialog" aria-label="Choisis ton dresseur">
      <header className="tp2-head">
        <button type="button" className="tp2-back" onClick={onClose}>← RETOUR</button>
        <h1>CHOISIS TON DRESSEUR</h1>
        <div className="tp2-gens" role="tablist" aria-label="Génération">
          {['TOUS', 1, 2, 3, 4, 5, 6, 7, 8, 9].map((g, i) => (
            <button key={g} type="button" role="tab" aria-selected={gen === i} title={i ? REGION_OF_GEN[i] : 'Toutes les générations'}
              className={gen === i ? 'is-on ah2-holo' : ''} onClick={() => setGen(i)}>{g}</button>
          ))}
        </div>
      </header>
      <div className="tp2-grid" role="listbox" aria-label="Dresseurs">
        {list.map((t) => (
          <button key={t.id} type="button" role="option" aria-selected={pick === t.id}
            className={`tp2-cell${pick === t.id ? ' is-on ah2-holo' : ''}`}
            onClick={() => setPick(t.id)} onDoubleClick={() => choose(t.id)}>
            <span className="tp2-cell-in">
              <TrainerImg t={t} />
              <b>{t.name}</b>
              <small>Gen {t.gen}{value === t.id ? ' · actuel' : ''}</small>
            </span>
          </button>
        ))}
      </div>
      <aside className="ah2-panel tp2-detail">
        <span className="tp2-lines" aria-hidden="true" />
        <b className="tp2-name">{cur.name}</b>
        <span className="tp2-tag ah2-holo">{REGION_OF_GEN[cur.gen]} · Gen {cur.gen}</span>
        <span className="tp2-frame ah2-holo"><span><TrainerImg key={cur.id} t={cur} className="is-big" /></span></span>
        <span className="tp2-bubble" aria-hidden="true"><i /><i /><i /></span>
        <button type="button" className="tp2-go ah2-holo" onClick={() => choose(cur.id)}>C’EST MOI !</button>
      </aside>
    </div>
  );
};

export const TrainerPicker = ({ value, onChange }) => {
  const [gen, setGen] = useState(0);
  const list = PLAYER_TRAINERS.filter((t) => !gen || t.gen === gen);
  return (
    <div className="ui-trainer-box">
      <div className="ui-trainer-gens" role="tablist" aria-label="Génération">
        {TRAINER_GENS.map(([g, label]) => (
          <button key={g} type="button" role="tab" aria-selected={gen === g} className={gen === g ? 'is-on' : ''} onClick={() => setGen(g)}>{label}</button>
        ))}
      </div>
      <div className="ui-trainer-pick" role="listbox" aria-label="Ton dresseur">
        {list.map((t) => (
          <button
            key={t.id}
            type="button"
            role="option"
            aria-selected={value === t.id}
            className={`ui-trainer-opt${value === t.id ? ' is-on' : ''}`}
            onClick={() => onChange(t.id)}
            title={t.name}
          >
            <TrainerPortrait trainer={{ sprite: t.sprite || t.id, name: t.name }} size="md" />
            <span>{t.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/** Starters dans l'ordre du Pokédex : Bulbizarre, Salamèche, Carapuce, Pikachu, Évoli, puis Johto, Hoenn… */
const STARTERS_BY_DEX = [...STARTER_LINES].sort((a, b) => (a.stages[0]?.dex || 0) - (b.stages[0]?.dex || 0));

export const StarterScreen = ({ onPick, onBack, onTrait, trainer, onTrainer }) => {
  const [gen, setGen] = useState(0);
  const [type, setType] = useState('');
  const [pickId, setPickId] = useState(STARTERS_BY_DEX[0]?.id);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const list = STARTERS_BY_DEX.filter((line) => {
    const first = line.stages[0];
    if (gen && genOf(first.dex) !== gen) return false;
    if (type) {
      const all = new Set();
      line.stages.forEach((s) => s.types.forEach((t) => all.add(t)));
      (line.forms || []).forEach((f) => f.stages.forEach((s) => s.types.forEach((t) => all.add(t))));
      if (!all.has(type)) return false;
    }
    return true;
  });
  const line = LINE_MAP[pickId] || list[0];
  const unit = line ? unitFromLine(line.id, 1, `starter-${line.id}`) : null;
  const evo = line ? evolutionPreview(line) : null;
  const typeFilters = ['Feu', 'Eau', 'Plante', 'Normal', 'Électrik'];
  return (
    <div className="ui-starter">
      <header className="ui-screen-head">
        <div>
          <h1>Choisis ton partenaire</h1>
          <p>Il commence sur le terrain. Deux autres starters, tirés au hasard, apparaîtront dans ta boutique.</p>
        </div>
        <button type="button" className="ui-btn is-ghost" onClick={onBack}>Retour</button>
      </header>
      {onTrainer && (
        <section className="ui-starter-trainer is-compact">
          <button type="button" className="tp2-open" onClick={() => setTrainerOpen(true)}>
            <TrainerPortrait trainer={trainerFace(PLAYER_TRAINERS.find((t) => t.id === trainer) || { id: trainer, name: trainer })} size="md" />
            <span><small>Ton dresseur</small><b>{(PLAYER_TRAINERS.find((t) => t.id === trainer) || {}).name || 'Choisir'}</b></span>
            <em>Changer ›</em>
          </button>
        </section>
      )}
      {trainerOpen && <TrainerScreen value={trainer} onChange={onTrainer} onClose={() => setTrainerOpen(false)} />}
      <div className="ui-starter-body">
        <section className="ui-starter-pick">
          <div className="ui-filters" role="toolbar" aria-label="Filtres">
            <button type="button" className={`ui-chip${gen === 0 ? ' is-on' : ''}`} onClick={() => setGen(0)}>Toutes les régions</button>
            {['Kanto', 'Johto', 'Hoenn', 'Sinnoh', 'Unys', 'Kalos', 'Alola', 'Galar', 'Paldea'].map((name, i) => (
              <button key={name} type="button" className={`ui-chip${gen === i + 1 ? ' is-on' : ''}`} onClick={() => setGen(i + 1)}>{name}</button>
            ))}
          </div>
          <div className="ui-filters" role="toolbar" aria-label="Type">
            <button type="button" className={`ui-chip${type === '' ? ' is-on' : ''}`} onClick={() => setType('')}>Tous les types</button>
            {typeFilters.map((t) => (
              <button key={t} type="button" className={`ui-chip is-type${type === t ? ' is-on' : ''}`} style={{ '--t': tint(t) }} onClick={() => setType(t)}>{t}</button>
            ))}
          </div>
          <div className="ui-starter-grid">
            {list.map((l) => {
              const u = unitFromLine(l.id, 1, `s-${l.id}`);
              return (
                <button
                  key={l.id}
                  type="button"
                  className={`ui-starter-card${line?.id === l.id ? ' is-on' : ''}`}
                  style={{ '--t': tint(u.type) }}
                  onClick={() => setPickId(l.id)}
                  onDoubleClick={() => onPick(l.id)}
                  aria-pressed={line?.id === l.id}
                >
                  <PokeSprite unit={u} size="md" />
                  <strong>{displayName(u.name)}</strong>
                  <span className="ui-dots">{u.types.map((t) => <i key={t} style={{ background: tint(t) }} title={t} />)}</span>
                </button>
              );
            })}
            {!list.length && <p className="ui-empty">Aucun starter avec ces filtres.</p>}
          </div>
        </section>
        {line && unit && (
          <section className="ui-starter-detail" style={{ '--t': tint(unit.type) }}>
            <div className="ui-starter-hero">
              <PokeSprite unit={{ ...unit, home: unit.art, art: unit.home }} size="xl" />
            </div>
            <h2>{displayName(unit.name)}</h2>
            <div className="ui-chips">
              {unit.types.map((t) => <TypeChip key={t} type={t} onHover={onTrait} />)}
              <span className={`ui-cost cost-${line.cost}`}>{line.cost} or</span>
            </div>
            <div className="ui-evo">
              {evo.pre.map((st, i) => (
                <React.Fragment key={st.dex}>
                  {i > 0 && <span className="ui-evo-arrow" aria-hidden>›</span>}
                  <MiniStage stage={st} />
                </React.Fragment>
              ))}
              {evo.branches.length > 0 && (
                <>
                  <span className="ui-evo-arrow" aria-hidden>›</span>
                  <div className={`ui-evo-branch${evo.branches.length > 3 ? ' is-wide' : ''}`}>
                    {evo.branches.map((st) => <MiniStage key={st.dex} stage={st} />)}
                  </div>
                </>
              )}
            </div>
            <div className="ui-stats">
              <StatRow label="PV" value={unit.hp} max={600} />
              <StatRow label="ATK" value={unit.atk} max={90} />
              <StatRow label="VIT" value={unit.spd} max={22} />
            </div>
            <p className="ui-muted">{linePlaystyle(line)} · {rangeLabel(line.range)}</p>
            <section className="ui-ability">
              <h4>{line.spell?.name}</h4>
              <p>{line.spell?.desc}</p>
            </section>
            {unit.ab && (
              <section className="ui-ability is-talent">
                <h4>Talent : {unit.ab.name}</h4>
                <p>{unit.ab.desc}</p>
              </section>
            )}
            <button type="button" className="ui-btn is-primary is-big" onClick={() => onPick(line.id)}>
              Partir avec {displayName(unit.name)}
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

/* ———————————————— Pool de la partie ———————————————— */

const lineTypes = (line) => {
  const all = [];
  const add = (st) => (st.types || [st.type]).forEach((t) => { if (t && !all.includes(t)) all.push(t); });
  line.stages.forEach(add);
  (line.forms || []).forEach((f) => f.stages.forEach(add));
  return all;
};

/** Toutes les lignées que la boutique peut proposer pendant cette partie, par coût. */
const pct = (v) => (v >= 10 || v === 0 ? `${Math.round(v)} %` : `${v.toFixed(1).replace('.', ',')} %`);

/** Chances par case de la boutique selon le coût, au niveau donné. */
export const ShopOdds = ({ level = 1, compact = false }) => {
  const odds = SHOP_ODDS[Math.min(9, Math.max(1, level))] || SHOP_ODDS[1];
  return (
    <div className={`ui-odds${compact ? ' is-compact' : ''}`} title={`Chances par case de la boutique au niveau ${level}`}>
      {[1, 2, 3, 4, 5].map((cost) => (
        <span key={cost} className={`ui-odds-cell cost-${cost}${odds[cost] ? '' : ' is-zero'}`}>
          {!compact && <b>{cost} or</b>}
          {odds[cost] || 0} %
        </span>
      ))}
    </div>
  );
};

export const PoolModal = ({ pool = [], owned = [], level = 1, onClose, onTrait }) => {
  useEscape(onClose);
  const [type, setType] = useState('');
  const counts = useMemo(() => {
    const map = {};
    owned.forEach((u) => { if (u?.lineId) map[u.lineId] = (map[u.lineId] || 0) + 1; });
    return map;
  }, [owned]);
  const rows = useMemo(() => pool.map((line) => {
    const unit = unitFromLine(line.id, 1, `pool-${line.id}`);
    const last = line.forms?.length ? null : line.stages[line.stages.length - 1];
    return { line, unit, types: lineTypes(line), finalName: last ? displayName(last.name) : 'Évolutions au choix' };
  }).filter((r) => r.unit), [pool]);
  const allTypes = useMemo(() => {
    const set = [];
    rows.forEach((r) => r.types.forEach((t) => { if (!set.includes(t)) set.push(t); }));
    return set.sort((a, b) => TYPES.indexOf(a) - TYPES.indexOf(b));
  }, [rows]);
  const shown = rows.filter((r) => !type || r.types.includes(type));
  return (
    <div className="ui-modal" role="dialog" aria-label="Pool de la partie" onClick={onClose}>
      <div className="ui-modal-card is-wide ui-pool" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Pool de la partie</h2>
          <button type="button" className="ui-btn is-ghost" onClick={onClose}>Fermer</button>
        </header>
        <p className="ui-muted">
          Les {pool.length} lignées que la boutique peut te proposer pendant cette partie. Celles que tu as déjà sont marquées.
        </p>
        <div className="ui-pool-odds">
          <span>Chances par case au niveau {level}</span>
          <ShopOdds level={level} />
          <small>
            La boutique a {SHOP_SIZE} cases. Chaque case tire d'abord un coût selon ces chances, puis une lignée au hasard parmi celles de ce coût.
          </small>
        </div>
        <div className="ui-filters" role="toolbar" aria-label="Filtrer par type">
          <button type="button" className={`ui-chip${type === '' ? ' is-on' : ''}`} onClick={() => setType('')}>Tous</button>
          {allTypes.map((t) => (
            <button key={t} type="button" className={`ui-chip is-type${type === t ? ' is-on' : ''}`} style={{ '--t': tint(t) }} onClick={() => setType(type === t ? '' : t)}>{t}</button>
          ))}
        </div>
        {[1, 2, 3, 4, 5].map((cost) => {
          const list = shown.filter((r) => r.line.cost === cost);
          if (!list.length) return null;
          const odds = (SHOP_ODDS[Math.min(9, Math.max(1, level))] || SHOP_ODDS[1])[cost] || 0;
          const inTier = rows.filter((r) => r.line.cost === cost).length || 1;
          const perSlot = odds / inTier;
          const perShop = (1 - (1 - perSlot / 100) ** SHOP_SIZE) * 100;
          return (
            <section key={cost} className="ui-pool-tier">
              <h3>
                <span className={`ui-cost cost-${cost}`}>{cost} or</span>
                <small>{list.length} lignée{list.length > 1 ? 's' : ''}</small>
                <em className={odds ? '' : 'is-zero'}>
                  {odds
                    ? `${odds} % par case · chaque espèce : ${pct(perSlot)} par case, ${pct(perShop)} d'être dans la boutique`
                    : `Pas encore au niveau ${level}`}
                </em>
              </h3>
              <div className="ui-pool-grid">
                {list.map(({ line, unit, types, finalName }) => (
                  <div key={line.id} className={`ui-pool-card${counts[line.id] ? ' is-owned' : ''}`} style={{ '--t': tint(unit.type) }}>
                    {counts[line.id] ? <span className="ui-pool-owned">×{counts[line.id]}</span> : null}
                    <PokeSprite unit={unit} size="md" />
                    <strong>{displayName(unit.name)}</strong>
                    <span className="ui-pool-final">› {finalName}</span>
                    <span className="ui-chips">{types.map((t) => <TypeChip key={t} type={t} small onHover={onTrait} />)}</span>
                    {(line.traits || []).filter((t) => !types.includes(t)).length > 0 && (
                      <span className="ui-pool-traits">{line.traits.filter((t) => !types.includes(t)).join(' · ')}</span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

/* ———————————————— Dresseurs ———————————————— */

/** Portrait de dresseur (sprite Showdown), avec repli sur l’initiale. */
export const TrainerPortrait = ({ trainer, size = 'md', title }) => {
  const list = (Array.isArray(trainer?.sprite) ? trainer.sprite : [trainer?.sprite]).filter(Boolean);
  const listKey = list.join('|');
  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setIdx(0); setFailed(false); }, [listKey]);
  const src = trainerSprite(list[idx]);
  const show = src && !failed;
  return (
    <span className={`ui-trainer-portrait is-${size}${show ? '' : ' is-fallback'}${trainer?.role ? ` role-${trainer.role}` : ''}`} title={title}>
      {show
        ? <img src={src} alt="" loading="lazy" draggable={false} onError={() => (idx + 1 < list.length ? setIdx(idx + 1) : setFailed(true))} />
        : <b aria-hidden>{(trainer?.name || '?').slice(0, 1)}</b>}
    </span>
  );
};

/** Boss d’une région (Champion d’Arène, ou Conseil 4 + Maître pour la Ligue). */
const regionBosses = (map, chapterId) => map
  .filter((f) => f.chapter === chapterId)
  .flatMap((f) => f.nodes.filter((n) => n.kind === 'boss').map((n) => n.trainer))
  .filter(Boolean);

/* ———————————————— Carte ———————————————— */

export const MapScreen = ({ map, floor, path, onEnter, hud, team }) => {
  const [focus, setFocus] = useState(null);
  const current = map[floor]?.nodes || [];
  const shown = focus || current[0] || null;
  return (
    <div className="ui-map">
      <div className="ui-map-main">
        {regionsOf(map).map((ch) => {
          const floors = map.filter((f) => f.chapter === ch.id);
          const done = floors.every((f) => f.floor < floor);
          const bosses = regionBosses(map, ch.id);
          return (
            <section key={ch.id} className={`ui-chapter biome-${ch.biome}${done ? ' is-done' : ''}`}>
              <header>
                <div>
                  <h2>{ch.name}</h2>
                  <p>{ch.blurb}</p>
                </div>
                {bosses.length > 0 && (
                  <div className="ui-chapter-bosses">
                    {bosses.map((t, i) => (
                      <TrainerPortrait key={`${t.name}-${i}`} trainer={t} size="sm" title={`${t.title} ${t.name}`} />
                    ))}
                  </div>
                )}
              </header>
              <ol className="ui-route">
                {floors.map((f) => (
                  <li key={f.floor} className={`ui-floor${f.floor === floor ? ' is-current' : ''}${f.floor < floor ? ' is-past' : ''}`}>
                    <span className="ui-floor-n">{f.floor + 1}</span>
                    <div className="ui-floor-nodes">
                      {f.nodes.map((node) => {
                        const chosen = path.includes(node.id);
                        const playable = f.floor === floor;
                        const meta = NODE_INFO[node.kind];
                        return (
                          <button
                            key={node.id}
                            type="button"
                            className={`ui-node kind-${node.kind}${chosen ? ' is-chosen' : ''}${playable ? ' is-playable' : ''}`}
                            disabled={!playable}
                            onClick={() => onEnter(node)}
                            onMouseEnter={() => setFocus(node)}
                            onFocus={() => setFocus(node)}
                            aria-label={`${meta.label}${node.trainer ? `, ${node.trainer.title} ${node.trainer.name}` : ''}`}
                          >
                            {node.trainer?.sprite && node.kind !== 'combat'
                              ? <TrainerPortrait trainer={node.trainer} size="xs" />
                              : <span className="ui-node-icon" aria-hidden>{meta.icon}</span>}
                            {(node.kind === 'boss' || node.kind === 'rival' || node.kind === 'villain') && <span className="ui-node-tag">{node.trainer.name}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          );
        })}
      </div>
      <aside className="ui-map-side">
        {hud}
        {shown && (
          <div className={`ui-node-info kind-${shown.kind}`}>
            {shown.trainer
              ? <TrainerPortrait trainer={shown.trainer} size="md" />
              : <span className="ui-node-icon" aria-hidden>{NODE_INFO[shown.kind].icon}</span>}
            <div>
              <h3>{NODE_INFO[shown.kind].label}{shown.floor === floor ? '' : ` (étage ${shown.floor + 1})`}</h3>
              <p>{NODE_INFO[shown.kind].hint}</p>
              {shown.trainer && (
                <>
                  <p className="ui-trainer"><b>{shown.trainer.title} {shown.trainer.name}</b>{shown.trainer.region && <em> · {shown.trainer.region}</em>}</p>
                  <div className="ui-chips">{shown.trainer.types.map((t) => <TypeChip key={t} type={t} small />)}</div>
                </>
              )}
            </div>
          </div>
        )}
        <div className="ui-map-team">
          <h3>Ton équipe</h3>
          <div className="ui-team-row">
            {team.length ? team.map((u) => (
              <span key={u.uid} className="ui-team-mon" title={`${displayName(u.name)} ${stars(u.star)}`}>
                <PokeSprite unit={u} size="sm" />
                <em>{stars(u.star)}</em>
              </span>
            )) : <p className="ui-empty">Personne pour l’instant.</p>}
          </div>
        </div>
        <p className="ui-muted">Choisis une étape de la colonne qui brille. Les combats te rapportent de l’or ; les Poké Marts vendent des objets.</p>
      </aside>
    </div>
  );
};

/* ———————————————— Recettes d’un objet ———————————————— */

/** Place une bulle fixe à côté d’un élément (à droite, sinon à gauche), dans l’écran. */
const floatBeside = (rect, w, h) => {
  if (!rect) return { left: 8, top: 8 };
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  let left = rect.right + 10;
  if (left + w > vw - 8) left = Math.max(8, rect.left - w - 10);
  const top = Math.max(8, Math.min(vh - h - 8, rect.top - 8));
  return { left, top };
};

/** Clic droit sur un objet : toutes les combinaisons possibles avec lui. */
export const ItemCraftsPopover = ({ itemId, rect, owned = [], onClose }) => {
  const item = ITEM_MAP[itemId];
  const ref = React.useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    const t = setTimeout(() => {
      window.addEventListener('pointerdown', close, true);
      window.addEventListener('contextmenu', close, true);
    }, 0);
    window.addEventListener('keydown', esc);
    window.addEventListener('resize', onClose);
    return () => {
      clearTimeout(t);
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('contextmenu', close, true);
      window.removeEventListener('keydown', esc);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);
  if (!item) return null;
  const list = craftsWith(itemId);
  const count = (id) => owned.filter((x) => x === id).length;
  const height = Math.min(460, 92 + list.length * 52);
  return (
    <div ref={ref} className="ui-crafts-pop" role="dialog" aria-label={`Recettes avec ${item.name}`} style={floatBeside(rect, 320, height)}>
      <header>
        <ItemIcon item={item} size={34} />
        <div>
          <strong>{item.name}</strong>
          <span>{list.length ? `${list.length} recette${list.length > 1 ? 's' : ''} possible${list.length > 1 ? 's' : ''}` : 'Ne se combine pas'}</span>
        </div>
      </header>
      {list.length ? (
        <ul>
          {list.map(({ other, result }) => {
            const have = count(other) - (other === itemId ? 1 : 0);
            return (
              <li key={`${other}-${result.id}`} className={have > 0 ? 'is-owned' : ''}>
                <span className="ui-crafts-eq">
                  <span className="ui-crafts-plus">+</span>
                  <ItemIcon item={ITEM_MAP[other]} size={26} />
                  <span className="ui-crafts-arrow">→</span>
                  <ItemIcon item={result} size={30} />
                </span>
                <span className="ui-crafts-txt">
                  <b>{result.name}</b>
                  <em>avec {ITEM_MAP[other].name}{have > 0 ? ' · dans ton sac' : ''}</em>
                  <small>{result.desc}</small>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="ui-crafts-none">{item.desc} Il s’utilise tel quel.</p>
      )}
      <p className="ui-crafts-foot">Pour combiner : équipe les deux objets sur le même Pokémon.</p>
    </div>
  );
};

/** Objet sélectionné + objet survolé : résultat de la combinaison. */
export const ComboPreview = ({ a, b, rect }) => {
  const A = ITEM_MAP[a];
  const B = ITEM_MAP[b];
  if (!A || !B) return null;
  const resultId = A.component && B.component ? craftResult(a, b) : null;
  const R = resultId ? ITEM_MAP[resultId] : null;
  return (
    <div className={`ui-combo-pop${R ? '' : ' is-none'}`} role="tooltip" style={floatBeside(rect, 280, 120)}>
      <div className="ui-combo-eq">
        <ItemIcon item={A} size={28} />
        <span>+</span>
        <ItemIcon item={B} size={28} />
        <span>→</span>
        {R ? <ItemIcon item={R} size={36} /> : <span className="ui-combo-x">✕</span>}
      </div>
      {R ? (
        <>
          <strong>{R.name}</strong>
          <p>{R.desc}</p>
        </>
      ) : (
        <p>{A.component && B.component ? 'Ces deux objets ne se combinent pas.' : 'Seules deux vitamines (ou deux Pierres Clés) se combinent.'}</p>
      )}
    </div>
  );
};

/* ———————————————— Poké Mart ———————————————— */

export const MartScreen = ({ stock, gold, onBuy, onReroll, onLeave, bag, hud, onItemMenu }) => (
  <div className="ui-mart">
    <header className="ui-screen-head">
      <div>
        <h1>Poké Mart</h1>
        <p>Les objets achetés vont dans ton sac. Équipe-les pendant la préparation d’un combat.</p>
      </div>
      <div className="ui-head-actions">
        <button type="button" className="ui-btn" onClick={onReroll} disabled={gold < 2}>Nouveaux articles (2 or)</button>
        <button type="button" className="ui-btn is-primary" onClick={onLeave}>Reprendre la route</button>
      </div>
    </header>
    {hud}
    <ul className="ui-shelf">
      {stock.map((slot) => {
        const it = ITEM_MAP[slot.itemId];
        if (!it) return null;
        const kind = it.component ? 'Vitamine' : it.plate ? 'Plaque' : it.consumable ? 'Consommable' : 'Objet tenu';
        return (
          <li key={slot.key} className={`ui-ware${slot.sold ? ' is-sold' : ''}`} onContextMenu={(e) => onItemMenu?.(slot.itemId, e)} title="Clic droit : recettes">
            <div className="ui-ware-icon"><ItemIcon item={it} size={48} /></div>
            <span className="ui-ware-kind">{kind}</span>
            <strong>{it.name}</strong>
            <p>{it.desc}</p>
            <button type="button" className="ui-btn is-buy" disabled={slot.sold || gold < slot.price} onClick={() => onBuy(slot)}>
              {slot.sold ? 'Vendu' : `${slot.price} or`}
            </button>
          </li>
        );
      })}
    </ul>
    <div className="ui-bag-preview">
      <h3>Ton sac</h3>
      <div className="ui-bag-row">
        {bag.length ? bag.map((id, i) => <span key={`${id}-${i}`} title={`${ITEM_MAP[id]?.name} · clic droit : recettes`} onContextMenu={(e) => onItemMenu?.(id, e)}><ItemIcon item={ITEM_MAP[id]} size={30} /></span>) : <p className="ui-empty">Vide.</p>}
      </div>
    </div>
  </div>
);

/* ———————————————— Événement ———————————————— */

export const EventScreen = ({ options, onChoose, hud, gold = 0 }) => (
  <div className="ui-event">
    <header className="ui-screen-head">
      <div>
        <h1>Une rencontre sur la route</h1>
        <p>Choisis une seule récompense.</p>
      </div>
    </header>
    {hud}
    <ul className="ui-event-opts">
      {options.map((o) => {
        const it = o.itemId ? ITEM_MAP[o.itemId] : null;
        const u = o.lineId ? unitFromLine(o.lineId, o.star || 1, `ev-${o.lineId}`) : null;
        return (
          <li key={o.id}>
            <button type="button" className="ui-event-card" disabled={!!o.cost && gold < o.cost} onClick={() => onChoose(o)}>
              <div className="ui-event-art">
                {it && <ItemIcon item={it} size={56} />}
                {u && <PokeSprite unit={u} size="lg" />}
                {!it && !u && <span className="ui-event-glyph" aria-hidden>{o.id === 'gold' ? '🪙' : o.id === 'xp' ? '📘' : o.id === 'tutor' ? '🥋' : '♨️'}</span>}
              </div>
              <strong>{o.title}</strong>
              <p>{o.text}</p>
              <span className="ui-event-reward">{o.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  </div>
);

/* ———————————————— Codex ———————————————— */


/* ——— Pokédex « Nuit holo » : grille de cartes + fiche interactive ——— */
const DexImg = ({ unit, big = false }) => {
  const chain = (big ? [unit?.ani, unit?.sprite, unit?.home] : [unit?.sprite, unit?.home]).filter(Boolean);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [unit?.dex]);
  if (idx >= chain.length) return <span className={big ? 'dx-big is-fb' : 'dx-mini is-fb'}>?</span>;
  return <img className={big ? 'dx-big' : 'dx-mini'} src={chain[idx]} alt="" draggable={false} loading="lazy" onError={() => setIdx((i) => i + 1)} />;
};
const lineSteps = (line) => {
  if (!line) return [];
  const out = [];
  if (line.forms) {
    for (let i = 1; i < line.branchAt; i += 1) out.push({ key: `s${i}`, star: i, unit: unitFromLine(line.id, i, `dx-${i}`) });
    for (let star = line.branchAt; star <= 3; star += 1) {
      line.forms.forEach((f) => out.push({ key: `s${star}-${f.id}`, star, unit: unitFromLine(line.id, star, `dx-${f.id}-${star}`, [], f.id) }));
    }
  } else {
    [1, 2, 3].forEach((star) => out.push({ key: `s${star}`, star, unit: unitFromLine(line.id, star, `dx-${star}`) }));
  }
  const formIds = line.forms?.length ? line.forms.map((f) => f.id) : [null];
  formIds.filter((fid) => canMegaEvolve(line.id, fid)).forEach((fid) => {
    const u = unitFromLine(line.id, 3, `dx-mega-${fid}`, ['megaring'], fid, !fid);
    if (u?.mega) out.push({ key: `mega-${fid}`, star: 4, mega: true, unit: u });
  });
  return out.filter((x) => x.unit);
};
let DEX_MAX = null;
const dexMax = () => {
  if (!DEX_MAX) {
    DEX_MAX = { hp: 1, atk: 1, spd: 1 };
    LINES.forEach((l) => {
      const u = unitFromLine(l.id, 3, `mx-${l.id}`);
      if (!u) return;
      DEX_MAX.hp = Math.max(DEX_MAX.hp, u.hp); DEX_MAX.atk = Math.max(DEX_MAX.atk, u.atk); DEX_MAX.spd = Math.max(DEX_MAX.spd, u.spd);
    });
  }
  return DEX_MAX;
};
const COST_COLOR = ['', '#b8c0d8', '#6fd29a', '#6fb6ff', '#c78bff', '#ffd25e'];

export const DexScreen = ({ onBack, onTrait }) => {
  const u = useStageUnit();
  const [cost, setCost] = useState(0);
  const [query, setQuery] = useState('');
  const [typeF, setTypeF] = useState('');
  const [pick, setPick] = useState(LINES[0]?.id);
  const [step, setStep] = useState(0);
  const lines = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LINES.filter((line) => {
      if (cost && line.cost !== cost) return false;
      const all = [...line.stages, ...(line.forms || []).flatMap((f) => f.stages)];
      if (typeF && !all.some((x) => x.types.includes(typeF)) && !(line.traits || []).includes(typeF)) return false;
      if (!q) return true;
      return `${all.map((x) => x.name).join(' ')} ${line.spell?.name || ''} ${line.ab?.name || ''}`.toLowerCase().includes(q);
    }).sort((a, b) => a.cost - b.cost || a.stages[0].name.localeCompare(b.stages[0].name, 'fr'));
  }, [cost, query, typeF]);
  const detail = (lines.find((l) => l.id === pick)) || lines[0] || null;
  const steps = useMemo(() => lineSteps(detail), [detail]);
  const cur = steps[Math.min(step, steps.length - 1)];
  const unit = cur?.unit;
  const max = dexMax();
  const rows = detail ? lineSynergyRows(detail) : [];
  const select = (id) => { setPick(id); setStep(0); };
  useEffect(() => {
    const k = (e) => {
      if (e.target.tagName === 'INPUT') { if (e.key === 'Escape') e.target.blur(); return; }
      const i = lines.findIndex((l) => l.id === detail?.id);
      if (e.key === 'Escape') onBack();
      else if (e.key === 'ArrowDown') { e.preventDefault(); if (lines[i + 1]) select(lines[i + 1].id); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (lines[i - 1]) select(lines[i - 1].id); }
      else if (e.key === 'ArrowRight') setStep((x) => Math.min(x + 1, steps.length - 1));
      else if (e.key === 'ArrowLeft') setStep((x) => Math.max(x - 1, 0));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });
  return (
    <div className="ah2 dx" style={{ '--u': `${u}px` }}>
      <header className="tp2-head dx-head">
        <button type="button" className="tp2-back" onClick={onBack}>← RETOUR</button>
        <h1>POKÉDEX</h1>
        <span className="dx-count">{lines.length} / {ROSTER_LINES} lignées</span>
        <input className="dx-search" type="search" placeholder="Chercher un Pokémon, un sort, un talent…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Rechercher" />
      </header>
      <div className="dx-left">
      <div className="dx-filters">
        <div className="dx-costs" role="group" aria-label="Coût">
          {[0, 1, 2, 3, 4, 5].map((c) => (
            <button key={c} type="button" className={cost === c ? 'is-on' : ''} style={{ '--c': COST_COLOR[c] || '#f3f0ff' }} onClick={() => setCost(cost === c ? 0 : c)}>{c ? `${c} or` : 'Tous'}</button>
          ))}
        </div>
        <div className="dx-types" role="group" aria-label="Type">
          {TYPES.map((t) => (
            <button key={t} type="button" className={typeF === t ? 'is-on' : ''} style={{ '--t': tint(t) }} onClick={() => setTypeF(typeF === t ? '' : t)}>{t}</button>
          ))}
        </div>
      </div>
      <div className="dx-grid">
        {lines.map((line) => {
          const b = unitFromLine(line.id, 1, `dg-${line.id}`);
          if (!b) return null;
          return (
            <button key={line.id} type="button" className={`dx-card${detail?.id === line.id ? ' is-on' : ''}`} style={{ '--t': tint(b.type), '--c': COST_COLOR[line.cost] }} onClick={() => select(line.id)}>
              <span className="dx-card-cost">{line.cost}</span>
              <DexImg unit={b} />
              <b>{displayName(b.name)}</b>
              <span className="dx-dots">{b.types.map((t) => <i key={t} style={{ background: tint(t) }} title={t} />)}</span>
            </button>
          );
        })}
        {!lines.length && <p className="dx-empty">Aucun Pokémon avec ces filtres.</p>}
      </div>
      </div>
      {detail && unit && (
        <aside className="ah2-panel dx-detail" style={{ '--t': tint(unit.type) }}>
          <span className="tp2-lines" aria-hidden="true" />
          <div className="dx-hero">
            <span className="dx-glow" />
            <DexImg key={unit.dex} unit={unit} big />
          </div>
          <div className="dx-title">
            <b>{displayName(unit.name)}</b>
            <span className="dx-chips">
              {unit.types.map((t) => <TypeChip key={t} type={t} small onHover={onTrait} />)}
              <span className="dx-cost" style={{ '--c': COST_COLOR[detail.cost] }}>{detail.cost} or</span>
            </span>
          </div>
          <div className={`dx-steps${steps.length > 6 ? ' is-many' : ''}`} role="tablist" aria-label="Évolutions">
            {[1, 2, 3, 4].map((star) => {
              const group = steps.map((st, i) => ({ st, i })).filter(({ st }) => st.star === star);
              if (!group.length) return null;
              return (
                <div key={star} className="dx-step-row">
                  {steps.length > 6 && <span className="dx-step-label">{star === 4 ? 'MÉGA' : stars(star)}</span>}
                  {group.map(({ st, i }) => (
                    <button key={st.key} type="button" role="tab" aria-selected={i === step} className={i === step ? 'is-on' : ''} onClick={() => setStep(i)} title={displayName(st.unit.name)}>
                      <DexImg unit={st.unit} />
                      {steps.length <= 6 && <small>{st.mega ? 'MÉGA' : stars(st.star)}</small>}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          <div className="dx-stats">
            {[['PV', unit.hp, max.hp], ['ATK', unit.atk, max.atk], ['VIT', unit.spd, max.spd]].map(([k, v, m]) => (
              <div key={k} className="dx-stat"><span>{k}</span><i><b style={{ width: `${Math.max(4, Math.min(100, (v / m) * 100))}%` }} /></i><em>{v}</em></div>
            ))}
          </div>
          <p className="dx-role">{linePlaystyle(detail)} · {rangeLabel(detail.range)}</p>
          <div className="dx-boxes">
            <section className="dx-box">
              <h4>{detail.spell?.name || 'Sort'}</h4>
              <p>{detail.spell?.desc}</p>
              <small>Ciblage : {AIM_LABELS[detail.spell?.aim] || '—'}</small>
            </section>
            {(unit.ab || detail.ab) && (
              <section className="dx-box is-talent">
                <h4>Talent · {(unit.ab || detail.ab).name}</h4>
                <p>{(unit.ab || detail.ab).desc}</p>
              </section>
            )}
          </div>
          <div className="dx-syn">
            {rows.map((r) => (
              <span key={r.trait} className="dx-syn-chip" style={{ '--t': tint(r.trait) }} title={r.tiers.map((t) => t.label).join(' ; ') || r.blurb}
                onMouseEnter={(e) => onTrait?.(r.trait, e)} onMouseLeave={() => onTrait?.(null)}>{r.trait}</span>
            ))}
          </div>
        </aside>
      )}
    </div>
  );
};

export const CodexScreen = ({ onBack, onTrait }) => {
  const [cost, setCost] = useState(0);
  const [query, setQuery] = useState('');
  const [typeF, setTypeF] = useState('');
  const [pick, setPick] = useState(LINES[0]?.id);
  const lines = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LINES.filter((line) => {
      if (cost && line.cost !== cost) return false;
      const allStages = [...line.stages, ...(line.forms || []).flatMap((f) => f.stages)];
      if (typeF && !allStages.some((s) => s.types.includes(typeF)) && !(line.traits || []).includes(typeF)) return false;
      if (!q) return true;
      const hay = `${allStages.map((s) => s.name).join(' ')} ${line.spell?.name || ''} ${line.ab?.name || ''} ${(line.forms || []).map((f) => f.ab?.name || '').join(' ')}`.toLowerCase();
      return hay.includes(q);
    }).sort((a, b) => a.cost - b.cost || a.stages[0].name.localeCompare(b.stages[0].name, 'fr'));
  }, [cost, query, typeF]);
  const detail = LINE_MAP[pick] || lines[0];
  const steps = detail
    ? (detail.forms
      ? [
        ...Array.from({ length: detail.branchAt - 1 }, (_, i) => ({ star: i + 1, units: [unitFromLine(detail.id, i + 1, `cx-${i}`)] })),
        ...Array.from({ length: 4 - detail.branchAt }, (_, k) => {
          const star = detail.branchAt + k;
          return { star, units: detail.forms.map((f) => unitFromLine(detail.id, star, `cx-${f.id}-${star}`, [], f.id)) };
        })
      ]
      : [1, 2, 3].map((star) => ({ star, units: [unitFromLine(detail.id, star, `cx-${star}`)] })))
    : [];
  const rows = detail ? lineSynergyRows(detail) : [];
  return (
    <div className="ui-codex">
      <header className="ui-screen-head">
        <div>
          <h1>Pokédex de l’arène</h1>
          <p>{lines.length} lignées affichées sur {ROSTER_LINES}. En run, ta boutique en propose ~{RUN_POOL_SIZE}.</p>
        </div>
        <button type="button" className="ui-btn is-ghost" onClick={onBack}>Retour</button>
      </header>
      <div className="ui-codex-body">
        <aside className="ui-codex-list">
          <input className="ui-search" type="search" placeholder="Nom, sort ou talent" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Rechercher" />
          <div className="ui-filters">
            {[0, 1, 2, 3, 4, 5].map((c) => (
              <button key={c} type="button" className={`ui-chip${cost === c ? ' is-on' : ''}`} onClick={() => setCost(c)}>{c ? `${c} or` : 'Tous'}</button>
            ))}
          </div>
          <select className="ui-select" value={typeF} onChange={(e) => setTypeF(e.target.value)} aria-label="Type">
            <option value="">Tous les types</option>
            {[...TYPES, 'Starter', 'Force', 'Mage'].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <ul>
            {lines.map((line) => {
              const u = unitFromLine(line.id, 1, `cl-${line.id}`);
              return (
                <li key={line.id}>
                  <button type="button" className={`ui-codex-row${detail?.id === line.id ? ' is-on' : ''}`} onClick={() => setPick(line.id)} style={{ '--t': tint(u.type) }}>
                    <PokeSprite unit={u} size="sm" />
                    <span>{displayName(u.name)}</span>
                    <em className={`ui-cost cost-${line.cost}`}>{line.cost}</em>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
        {detail && (
          <section className="ui-codex-detail">
            <div className="ui-codex-steps">
              {steps.map((step) => (
                <div key={step.star} className="ui-codex-step">
                  <span className="ui-stars">{stars(step.star)}</span>
                  <div className="ui-codex-forms">
                    {step.units.filter(Boolean).map((u) => (
                      <figure key={u.uid} style={{ '--t': tint(u.type) }}>
                        <PokeSprite unit={u} size="lg" />
                        <figcaption>
                          <b>{displayName(u.name)}</b>
                          <span className="ui-chips">{u.types.map((t) => <TypeChip key={t} type={t} small onHover={onTrait} />)}</span>
                          <small>PV {u.hp} · ATK {u.atk} · VIT {u.spd}</small>
                          {detail.forms && u.ab && u.ab !== detail.ab && <small>Talent : {u.ab.name}</small>}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              ))}
              {(() => {
                const formIds = detail.forms?.length ? detail.forms.map((f) => f.id) : [null];
                const megas = formIds
                  .filter((fid) => canMegaEvolve(detail.id, fid))
                  .map((fid) => unitFromLine(detail.id, 3, `mega-${detail.id}-${fid}`, ['megaring'], fid, !fid))
                  .filter((u) => u?.mega);
                if (!megas.length) return null;
                return (
                  <div className="ui-codex-step is-mega">
                    <span className="ui-stars" title="Avec le Bracelet Méga">✦</span>
                    <div className="ui-codex-forms">
                      {megas.map((u) => (
                        <figure key={u.uid} style={{ '--t': tint(u.type) }}>
                          <PokeSprite unit={u} size="lg" />
                          <figcaption>
                            <b>{displayName(u.name)}</b>
                            <span className="ui-chips">{u.types.map((t) => <TypeChip key={t} type={t} small onHover={onTrait} />)}</span>
                            <small>Avec le Bracelet Méga (2 Pierres Clés) · PV {u.hp} · ATK {u.atk}</small>
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="ui-codex-text">
              <section className="ui-ability">
                <h4>{detail.spell?.name}</h4>
                <p>{detail.spell?.desc}</p>
                <p className="ui-muted">Ciblage : {AIM_LABELS[detail.spell?.aim] || '—'}. {rangeLabel(detail.range)} ; ses sorts portent à {(detail.range || 1) + 1} cases.</p>
              </section>
              {detail.ab && (
                <section className="ui-ability is-talent">
                  <h4>Talent : {detail.ab.name}</h4>
                  <p>{detail.ab.desc}</p>
                  {(detail.forms || []).some((f) => f.ab && f.ab !== detail.ab) && (
                    <ul>
                      {detail.forms.map((f) => <li key={f.id}><b>{displayName(f.stages[0].name)}</b> : {f.ab?.name} — {f.ab?.desc}</li>)}
                    </ul>
                  )}
                </section>
              )}
              <section className="ui-codex-syn">
                <h4>Synergies</h4>
                <ul>
                  {rows.map((r) => (
                    <li key={r.trait} style={{ '--t': tint(r.trait) }}>
                      <TypeChip type={r.trait} small onHover={onTrait} />
                      <span>{r.tiers.map((t) => t.label).join(' ; ') || r.blurb}</span>
                      <em>{r.where.join(', ')}</em>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

/* ———————————————— Résultat d’un combat ———————————————— */

export const ResultOverlay = ({ result, onContinue, continueLabel }) => {
  if (!result) return null;
  const { win, gains, loot, hpLost, meter, boss } = result;
  return (
    <div className="ui-result" role="dialog" aria-label={win ? 'Victoire' : 'Défaite'}>
      <div className={`ui-result-card${win ? ' is-win' : ' is-loss'}`}>
        {win && <div className="ui-confetti" aria-hidden>{Array.from({ length: 24 }).map((_, i) => <i key={i} style={{ '--i': i }} />)}</div>}
        <h2>{win ? 'Victoire !' : 'Défaite…'}</h2>
        {boss && win && <p className="ui-result-sub">Tu as battu {boss} ! Choisis un objet en récompense.</p>}
        {!win && boss && <p className="ui-result-sub">Le champion bloque la route : retente ta chance.</p>}
        <ul className="ui-result-lines">
          {gains.total > 0 ? (
            <li><span>Or gagné</span><strong>+{gains.total}</strong><em>base {gains.base}, intérêts {gains.interest}, série {gains.streakBonus}{gains.winBonus ? `, victoire ${gains.winBonus}` : ''}</em></li>
          ) : (
            <li className="is-bad"><span>Or gagné</span><strong>0</strong><em>une défaite ne rapporte pas d’or</em></li>
          )}
          <li><span>Expérience</span><strong>+2 XP</strong></li>
          {hpLost > 0 && <li className="is-bad"><span>PV de dresseur</span><strong>-{hpLost}</strong></li>}
          {loot.length > 0 && (
            <li><span>Butin</span><strong className="ui-loot">{loot.map((id, i) => <ItemIcon key={`${id}-${i}`} item={ITEM_MAP[id]} size={28} />)}</strong></li>
          )}
        </ul>
        {meter.length > 0 && (
          <div className="ui-meter is-result">
            <h3>Dégâts infligés</h3>
            {meter.slice(0, 5).map((m) => (
              <div key={m.uid} className="ui-meter-row">
                <span>{displayName(m.name)}</span>
                <i><b style={{ width: `${(m.dmg / Math.max(1, meter[0].dmg)) * 100}%` }} /></i>
                <strong>{m.dmg}</strong>
              </div>
            ))}
          </div>
        )}
        <button type="button" className="ui-btn is-primary is-big" onClick={onContinue} autoFocus>{continueLabel}</button>
      </div>
    </div>
  );
};

export const RewardModal = ({ choices, onPick }) => (
  <div className="ui-modal" role="dialog" aria-label="Récompense">
    <div className="ui-modal-card">
      <header><h2>Récompense du champion</h2></header>
      <ul className="ui-reward">
        {choices.map((id) => {
          const it = ITEM_MAP[id];
          return (
            <li key={id}>
              <button type="button" className="ui-reward-card" onClick={() => onPick(id)}>
                <ItemIcon item={it} size={52} />
                <strong>{it.name}</strong>
                <span>{it.desc}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  </div>
);

export const chapterLabel = (floor, map = []) => regionAt(map, floor)?.name || '';

export { isFightKind };

/* ——————————————————————————— Salon multi ——————————————————————————— */

/** Écran du salon : choisir un pseudo, créer ou rejoindre par code, puis attendre le lancement. */
export const VersusLobby = ({ room, me, name, onName, onCreate, onJoin, onStart, onLeave, error, busy, poolPreview }) => {
  const [code, setCode] = useState('');
  const isHost = room && room.hostId === me;
  const players = room?.players || [];
  return (
    <div className="ui-lobby">
      <header className="ui-screen-head">
        <div>
          <h1>Salon multi</h1>
          <p>De 2 à 8 dresseurs. Un pool de Pokémon est tiré au hasard au lancement, le même pour tout le monde, et chacun commence avec un starter du pool tiré au sort.</p>
        </div>
        <button type="button" className="ui-btn is-ghost" onClick={onLeave}>Retour</button>
      </header>

      {!room ? (
        <div className="ui-lobby-entry">
          <label className="ui-field">
            <span>Ton pseudo</span>
            <input className="ui-search" value={name} maxLength={20} onChange={(e) => onName(e.target.value)} placeholder="Sacha" />
          </label>
          <div className="ui-lobby-choices">
            <section className="ui-card">
              <h3>Créer un salon</h3>
              <p className="ui-muted">Tu recevras un code à 4 lettres à partager.</p>
              <button type="button" className="ui-btn is-primary is-big" onClick={onCreate} disabled={busy || !name.trim()}>Créer</button>
            </section>
            <section className="ui-card">
              <h3>Rejoindre</h3>
              <input
                className="ui-search ui-code-input"
                value={code}
                maxLength={4}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                placeholder="CODE"
                aria-label="Code du salon"
              />
              <button type="button" className="ui-btn is-big" onClick={() => onJoin(code)} disabled={busy || code.length !== 4 || !name.trim()}>Rejoindre</button>
            </section>
          </div>
          {error && <p className="ui-lobby-error" role="alert">{error}</p>}
        </div>
      ) : (
        <div className="ui-lobby-room">
          <section className="ui-card ui-lobby-code">
            <h3>Code du salon</h3>
            <strong>{room.code}</strong>
            <p className="ui-muted">Partage ce code : tes amis le saisissent dans « Rejoindre ».</p>
          </section>
          <section className="ui-card">
            <h3>Dresseurs ({players.length} / 8)</h3>
            <ul className="ui-lobby-players">
              {players.map((p) => (
                <li key={p.id} className={p.id === me ? 'is-me' : ''}>
                  <span>{p.name}</span>
                  {p.id === room.hostId && <em>Chef</em>}
                  {p.id === me && <em>Toi</em>}
                </li>
              ))}
            </ul>
            {poolPreview && <p className="ui-muted">Pool à {players.length} joueurs : {poolPreview.size} lignées dont {poolPreview.starters} starters.</p>}
            {isHost ? (
              <button type="button" className="ui-btn is-primary is-big" onClick={onStart} disabled={busy || players.length < 2}>
                {players.length < 2 ? 'En attente d’un 2e joueur…' : 'Lancer la partie'}
              </button>
            ) : (
              <p className="ui-muted">Le chef du salon lance la partie.</p>
            )}
            {error && <p className="ui-lobby-error" role="alert">{error}</p>}
          </section>
        </div>
      )}
    </div>
  );
};

/** Classement en direct pendant une partie multi. */
export const VersusPlayers = ({ room, me }) => {
  if (!room) return null;
  const players = [...room.players].sort((a, b) => (b.alive - a.alive) || b.hp - a.hp);
  return (
    <div className="ui-card ui-vs-players">
      <h3>Dresseurs</h3>
      <ul>
        {players.map((p) => (
          <li key={p.id} className={`${p.id === me ? 'is-me' : ''}${p.alive ? '' : ' is-out'}`}>
            <span className="ui-vs-name">{p.name}{!p.connected && ' (parti)'}</span>
            <i><b style={{ width: `${Math.max(0, p.hp)}%` }} /></i>
            <strong>{p.alive ? p.hp : `${p.place || '—'}e`}</strong>
            {room.status === 'prep' && p.alive && <em className={p.ready ? 'is-ready' : ''}>{p.ready ? 'Prêt' : '…'}</em>}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Bilan d’une partie multi. */
export const VersusOver = ({ ranking, me, onBack, onLobby, team }) => {
  const mine = ranking?.find((r) => r.id === me);
  const first = mine?.place === 1;
  return (
    <div className={`pa-over${first ? ' is-win' : ''}`}>
      {first && <div className="ui-confetti" aria-hidden>{Array.from({ length: 40 }).map((_, i) => <i key={i} style={{ '--i': i }} />)}</div>}
      <h1>{!ranking ? 'Éliminé' : first ? 'Victoire !' : `${mine?.place || '?'}e place`}</h1>
      <p>{ranking ? 'Classement final du salon.' : 'Tu es éliminé. Le classement s’affichera à la fin de la partie.'}</p>
      {ranking && (
        <ol className="ui-vs-ranking">
          {ranking.map((r) => (
            <li key={r.id} className={r.id === me ? 'is-me' : ''}>
              <strong>{r.place || '—'}</strong>
              <span>{r.name}</span>
              <em>{r.hp} PV</em>
            </li>
          ))}
        </ol>
      )}
      {team?.length > 0 && (
        <div className="pa-over-team">
          {team.map((u) => (
            <figure key={u.uid}>
              <PokeSprite unit={u} size="lg" />
              <figcaption>{displayName(u.name)} <span className="ui-stars">{stars(u.star)}</span></figcaption>
            </figure>
          ))}
        </div>
      )}
      <div className="pa-hub-actions">
        <button type="button" className="ui-btn is-primary is-big" onClick={onLobby}>Nouveau salon</button>
        <button type="button" className="ui-btn" onClick={onBack}>Menu</button>
      </div>
    </div>
  );
};
